import type { ContentItemType } from '../generated/social-media/metapicSocialMediaAPIDocumentation.schemas'
import {
  isSupportedContentItemPlatform,
  orderContentTypes,
  platformOfContentType,
  platformOfContentTypes,
  platformToContentTypes,
} from '../utils/content-item-platform.utils'

import type { QueryFilterField, QueryValue } from './useQueryFilter'

const SEPARATOR = ','

const tokenize = (raw: QueryValue): string[] => {
  if (raw === undefined || raw === null) {
    return []
  }

  const entries = Array.isArray(raw) ? raw : [raw]

  return entries
    .filter((entry): entry is string => typeof entry === 'string')
    .flatMap((entry) => entry.split(SEPARATOR))
    .map((token) => token.trim())
    .filter((token) => token.length > 0)
}

/**
 * The `source` query key, holding either a platform (`instagram`) or a list of that platform's
 * content types (`instagram_reel,instagram_post`), always parsed to a full `ContentItemType[]`.
 * Anything that cannot round-trip to exactly one URL — mixed platforms, mixed granularity, unknown
 * tokens — parses to `undefined` rather than being repaired.
 */
export const contentTypesField = (
  key = 'source',
): QueryFilterField<ContentItemType[] | undefined> => ({
  // Reads `key` out of `query`, not the positional `raw`, like `entityField`: `parseAll` passes
  // `query[schemaKey]`, and this field's schema key (`contentTypes`) is not its query key.
  parse: (_raw, query) => {
    const tokens = tokenize(query[key])
    if (tokens.length === 0) {
      return undefined
    }

    if (tokens.every(isSupportedContentItemPlatform)) {
      return tokens.length === 1 && tokens[0] !== undefined
        ? platformToContentTypes(tokens[0])
        : undefined
    }

    if (!tokens.every((token) => platformOfContentType(token) !== undefined)) {
      return undefined
    }

    return platformOfContentTypes(tokens) ? orderContentTypes(tokens) : undefined
  },
  serialize: (value, query) => {
    const platform = platformOfContentTypes(value)
    if (!platform || !value?.length) {
      delete query[key]
      return
    }

    const ordered = orderContentTypes(value)
    const isWholePlatform = ordered.length === platformToContentTypes(platform).length

    query[key] = isWholePlatform ? platform : ordered.join(SEPARATOR)
  },
})
